import React, { useState, useRef, useEffect } from 'react'

interface DateRangePickerProps {
  from: string
  to: string
  onFromChange: (val: string) => void
  onToChange: (val: string) => void
  maxDate?: string
  style?: React.CSSProperties
}

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

// Local-date (not UTC) YMD — callers must use this same helper for their default
// from/to values, otherwise "today" here and "today" there disagree for any user
// whose local timezone offset crosses midnight before/after UTC does.
export const toYMD = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
const parseYMD = (s: string) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, (m ?? 1) - 1, d ?? 1) }
const fmtDisplay = (s: string) => { if (!s) return 'Select date'; const d = parseYMD(s); return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}` }

const TRIGGER_STYLE: React.CSSProperties = {
  padding: '0.25rem 0.6rem',
  borderRadius: 6,
  fontSize: '0.75rem',
  background: 'var(--bg-elevated)',
  border: '1px solid var(--border)',
  color: 'var(--text-secondary)',
  cursor: 'pointer',
  fontFamily: 'inherit',
  display: 'flex',
  alignItems: 'center',
  gap: '0.35rem',
  whiteSpace: 'nowrap',
}

// Own calendar UI — a native <input type="date"> can't be used here because
// some browsers' built-in pickers jump the selected value to the 1st of the
// month just from paging between months, with no way to intercept that.
function CalendarPopover({ value, min, max, onChange, onClose }: {
  value: string
  min?: string
  max?: string
  onChange: (val: string) => void
  onClose: () => void
}) {
  const selected = value ? parseYMD(value) : new Date()
  const [viewYear, setViewYear]   = useState(selected.getFullYear())
  const [viewMonth, setViewMonth] = useState(selected.getMonth())

  const minDate = min ? parseYMD(min) : null
  const maxDate = max ? parseYMD(max) : null
  const today   = toYMD(new Date())

  // Navigating months only ever changes what's displayed — never the selected value.
  const goPrevMonth = () => { const d = new Date(viewYear, viewMonth - 1, 1); setViewYear(d.getFullYear()); setViewMonth(d.getMonth()) }
  const goNextMonth = () => { const d = new Date(viewYear, viewMonth + 1, 1); setViewYear(d.getFullYear()); setViewMonth(d.getMonth()) }

  const monthStart   = new Date(viewYear, viewMonth, 1)
  const gridStart     = new Date(viewYear, viewMonth, 1 - monthStart.getDay())
  const cells: Date[] = Array.from({ length: 42 }, (_, i) => new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i))

  const isDisabled = (d: Date) => (minDate !== null && d < minDate) || (maxDate !== null && d > maxDate)

  const handleSelect = (d: Date) => {
    if (isDisabled(d)) return
    onChange(toYMD(d))
    onClose()
  }

  const handleToday = () => {
    const now = new Date()
    if (isDisabled(now)) return
    setViewYear(now.getFullYear()); setViewMonth(now.getMonth())
    onChange(toYMD(now))
    onClose()
  }

  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: '0.75rem', width: '260px', boxShadow: 'var(--shadow-elevated, 0 8px 24px rgba(0,0,0,0.3))' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.6rem' }}>
        <button onClick={goPrevMonth} title="Previous month" style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.9rem', padding: '0.2rem 0.4rem', borderRadius: 4 }}>‹</button>
        <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-primary)' }}>{MONTH_NAMES[viewMonth]} {viewYear}</span>
        <button onClick={goNextMonth} title="Next month" style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.9rem', padding: '0.2rem 0.4rem', borderRadius: 4 }}>›</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', marginBottom: '0.2rem' }}>
        {WEEKDAYS.map(w => (
          <div key={w} style={{ textAlign: 'center', fontSize: '0.65rem', fontWeight: 700, color: 'var(--text-muted)', padding: '0.2rem 0' }}>{w}</div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
        {cells.map((d, i) => {
          const ymd = toYMD(d)
          const inMonth  = d.getMonth() === viewMonth
          const isSel    = ymd === value
          const isToday  = ymd === today
          const disabled = isDisabled(d)
          return (
            <button
              key={i}
              onClick={() => handleSelect(d)}
              disabled={disabled}
              style={{
                textAlign: 'center', fontSize: '0.72rem', padding: '0.35rem 0', borderRadius: 6, border: 'none',
                cursor: disabled ? 'not-allowed' : 'pointer',
                background: isSel ? 'var(--accent-primary)' : 'transparent',
                color: disabled ? 'var(--text-muted)' : isSel ? 'white' : inMonth ? 'var(--text-primary)' : 'var(--text-muted)',
                opacity: disabled ? 0.35 : inMonth ? 1 : 0.5,
                fontWeight: isToday && !isSel ? 700 : 400,
                outline: isToday && !isSel ? '1px solid var(--accent-primary)' : 'none',
                outlineOffset: '-1px',
              }}
              onMouseEnter={e => { if (!disabled && !isSel) (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-elevated)' }}
              onMouseLeave={e => { if (!disabled && !isSel) (e.currentTarget as HTMLButtonElement).style.background = 'transparent' }}
            >
              {d.getDate()}
            </button>
          )
        })}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid var(--border)' }}>
        <button onClick={handleToday} disabled={isDisabled(new Date())}
          style={{ background: 'transparent', border: 'none', color: isDisabled(new Date()) ? 'var(--text-muted)' : 'var(--accent-primary)', fontSize: '0.75rem', fontWeight: 600, cursor: isDisabled(new Date()) ? 'not-allowed' : 'pointer' }}>
          Today
        </button>
      </div>
    </div>
  )
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  from, to, onFromChange, onToChange, maxDate, style,
}) => {
  const today = toYMD(new Date())
  const max = maxDate ?? today
  const [openField, setOpenField] = useState<'from' | 'to' | null>(null)
  const wrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpenField(null)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  return (
    <div ref={wrapRef} style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', ...style }}>
      <div style={{ position: 'relative' }}>
        <button onClick={() => setOpenField(f => f === 'from' ? null : 'from')} style={TRIGGER_STYLE}>
          📅 {fmtDisplay(from)}
        </button>
        {openField === 'from' && (
          <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 50 }}>
            <CalendarPopover value={from} max={to} onChange={onFromChange} onClose={() => setOpenField(null)} />
          </div>
        )}
      </div>

      <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>to</span>

      <div style={{ position: 'relative' }}>
        <button onClick={() => setOpenField(f => f === 'to' ? null : 'to')} style={TRIGGER_STYLE}>
          📅 {fmtDisplay(to)}
        </button>
        {openField === 'to' && (
          <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 50 }}>
            <CalendarPopover value={to} min={from} max={max} onChange={onToChange} onClose={() => setOpenField(null)} />
          </div>
        )}
      </div>
    </div>
  )
}
